import classNames from 'classnames/bind';
import { useState } from 'react';

import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import styles from './Geocode.module.scss';
import TextInput from 'components/helpers/TextInput/TextInput';

import Select from 'components/helpers/Select/Select';
import { GeocodeResponse } from './Types/Geocode.Type';
import { dataFetcherGet } from 'lib/data-fetcher';

type Coordinates = {
  lat: number;
  lng: number;
};

// Convert degrees → radians
const toRadians = (deg: number) => (deg * Math.PI) / 180;

// Distance in miles
export const calculateDistance = (point1: Coordinates, point2: Coordinates): number => {
  const R = 3958.8; // Earth radius in miles

  const dLat = toRadians(point2.lat - point1.lat);
  const dLng = toRadians(point2.lng - point1.lng);

  const lat1 = toRadians(point1.lat);
  const lat2 = toRadians(point2.lat);

  const a = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

type EventAddressItem = {
  id: string;
  title: string;
  latitude: number;
  longitude: number;
  distance?: number; // distance from user location, to be calculated
};

export const filterEventsByRadius = (userLocation: Coordinates, events: EventAddressItem[], radius: number) => {
  return events
    .map((event) => {
      const distance = calculateDistance(userLocation, {
        lat: event.latitude,
        lng: event.longitude,
      });

      return {
        ...event,
        distance,
      };
    })
    .filter((event) => event.distance <= radius)
    .sort((a, b) => a.distance - b.distance); // nearest first
};

export type DistanceFilterProps = {
  headerTitle: string;
};

const DistanceFilter: React.FC<DistanceFilterProps> = (props) => {
  const cx = classNames.bind(styles);

  const [userAddress, setUserAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [radius, setRadius] = useState(10); // default 10 miles
  const [filteredEvents, setFilteredEvents] = useState<EventAddressItem[]>([]);
  const radiusOptions = [
    { label: { value: '5 miles' }, value: '5' },
    { label: { value: '10 miles' }, value: '10' },
    { label: { value: '25 miles' }, value: '25' },
    { label: { value: '50 miles' }, value: '50' },
  ];
  const events: EventAddressItem[] = [
    //  Within 5 miles
    { id: '1', title: 'Event at Downtown Pittsburgh', latitude: 40.4406, longitude: -79.9959 },
    { id: '2', title: 'Event at Oakland (Universities)', latitude: 40.444, longitude: -79.9608 },
    { id: '3', title: 'Event at South Side', latitude: 40.4284, longitude: -79.98 },

    // Within 10 miles
    { id: '4', title: 'Event at Mount Lebanon', latitude: 40.3753, longitude: -80.0428 },
    { id: '5', title: 'Event at Penn Hills', latitude: 40.5012, longitude: -79.8392 },
    { id: '6', title: 'Event at Monroeville', latitude: 40.4212, longitude: -79.7881 },

    // Within 25 miles
    { id: '7', title: 'Event at Cranberry Township', latitude: 40.6848, longitude: -80.1073 },
    { id: '8', title: 'Event at Washington, PA', latitude: 40.1739, longitude: -80.2462 },
    { id: '9', title: 'Event at Greensburg, PA', latitude: 40.3015, longitude: -79.5389 },
  ];

  const handleAddressSearch = async () => {
    setError('');

    if (!userAddress.trim()) {
      setError('Please enter an address.');
      return;
    }
    if (!isValidAddress(userAddress)) {
      setError('Please enter a valid zip code.');
      return;
    }

    setFilteredEvents([]);

    try {
      setLoading(true);

      const geocodeApiUrl = `/api/maps/geocodeByAzureMapService?query=${encodeURIComponent(userAddress.trim())}`;
      const response = await dataFetcherGet<GeocodeResponse>(geocodeApiUrl);

      const results = response?.data?.geocodeData?.results;

      if (results?.length > 0) {
        const [lng, lat] = results[0].geometry?.coordinates || [];
        const coords = { lat, lng }; // For POC considering first result
        return coords;
      } else {
        setError('No results found for the provided address.');
        return null;
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      setError('Something went wrong. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDistanceFilter = async () => {
    const coords = await handleAddressSearch(); //wait for it

    if (!coords || coords.lat == null || coords.lng == null) return;

    const results = filterEventsByRadius(coords, events, radius);
    setFilteredEvents(results);
  };

  const isValidAddress = (address: string) => {
    const givenAddress = address.trim();
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;
    if (zipCodeRegex.test(givenAddress)) return true;

    // Basic validation for city/state or full address (can be improved with more complex regex)
    const cityStateRegex = /^[a-zA-Z\s]+,\s*[A-Z]{2}$/;
    if (cityStateRegex.test(givenAddress)) return true;

    // Address line (must contain a number + some text)
    const hasNumber = /\d/;
    if (hasNumber.test(givenAddress) && givenAddress.length > 5) return true;

    return false;
  };

  return (
    <div className={cx('address-search')}>
      <h2 className={cx('header')}>{props.headerTitle}</h2>
      <label htmlFor="userAddress" className={cx('address-label')}>
        Enter Address
      </label>

      <TextInput
        value={userAddress}
        type="text"
        id="userAddress"
        onChange={(e) => setUserAddress(e.target.value)}
        placeholder="Zip or City/State or full address"
        className={cx('inputBox')}
      />
      <p className={cx('address-label')}>
        Please use 15213/15222 or Pittsburgh, PA or 600 grant street, Pittsburgh, PA for best testing results.
      </p>
      <Select
        id="radius"
        label="Select Radius"
        value={radius.toString()}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRadius(Number(e.target.value))}
        className={cx('selectBox')}
        options={radiusOptions}
      />

      <Button variant={ButtonVariant.Primary} onClick={handleDistanceFilter} disabled={loading}>
        {loading ? 'Filtering...' : 'Filter by Distance'}
      </Button>

      {error && <p className={cx('error-msg')}>{error}</p>}

      {filteredEvents.length > 0 ? (
        filteredEvents.map((event) => (
          <div key={event.id}>
            <p>
              <strong>{event.title}</strong>
            </p>
            <p>Distance: {event.distance?.toFixed(2)} miles</p>
          </div>
        ))
      ) : (
        <p>No events found within selected radius.</p>
      )}
    </div>
  );
};

export default DistanceFilter;
